import OpenAI from 'openai';
import { supabase } from '../lib/supabase';

// Initialize AIMLAPI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_AIMLAPI_KEY,
  baseURL: 'https://api.aimlapi.com/v1',
  dangerouslyAllowBrowser: true
});

/**
 * Embedding Generation Service
 * Generates and caches vector embeddings for semantic search
 * Uses AIMLAPI's text-embedding-ada-002 model (1536 dimensions)
 */

const EMBEDDING_MODEL = 'text-embedding-ada-002';
const EMBEDDING_DIMENSION = 1536;
const BATCH_SIZE = 100; // OpenAI-compatible API allows batching
const COST_PER_1K_TOKENS = 0.0001; // Competitive AIMLAPI pricing

/**
 * Generate embedding for a single text
 * @param {string} text - Text to embed
 * @returns {Promise<Array<number>>} - Embedding vector
 */
export const generateEmbedding = async (text) => {
  if (!text || text.trim().length === 0) {
    throw new Error('Cannot generate embedding for empty text');
  }

  try {
    const response = await openai.embeddings.create({
      model: EMBEDDING_MODEL,
      input: text.substring(0, 8000), // Truncate to avoid token limits
    });

    return response.data[0].embedding;
  } catch (error) {
    console.error('Error generating embedding:', error);
    throw error;
  }
};

/**
 * Generate embeddings for multiple texts in batch
 * @param {Array<string>} texts - Array of texts to embed
 * @returns {Promise<Array<Array<number>>>} - Array of embedding vectors
 */
export const generateEmbeddingsBatch = async (texts) => {
  if (!texts || texts.length === 0) {
    return [];
  }

  // Filter out empty texts
  const validTexts = texts.filter(t => t && t.trim().length > 0);
  
  if (validTexts.length === 0) {
    return [];
  }

  try {
    // Truncate texts to avoid token limits
    const truncatedTexts = validTexts.map(t => t.substring(0, 8000));

    const response = await openai.embeddings.create({
      model: EMBEDDING_MODEL,
      input: truncatedTexts,
    });

    return response.data.map(item => item.embedding);
  } catch (error) {
    console.error('Error generating embeddings batch:', error);
    throw error;
  }
};

/**
 * Generate and store embeddings for a response unit
 * @param {string} responseUnitId - Response unit ID
 * @param {Object} responseUnit - Response unit data
 * @returns {Promise<Object>} - Embedding metadata
 */
export const generateResponseUnitEmbeddings = async (responseUnitId, responseUnit) => {
  try {
    const embeddings = {};
    const embeddingTypes = [];
    let totalTokens = 0;

    // 1. Content embedding (title + phases)
    const contentText = buildContentText(responseUnit);
    if (contentText) {
      embeddings.content = await generateEmbedding(contentText);
      embeddingTypes.push('content');
      totalTokens += estimateTokens(contentText);
    }

    // 2. Objection embedding (for objection responses)
    if (responseUnit.type === 'objection') {
      const objectionText = buildObjectionText(responseUnit);
      if (objectionText) {
        embeddings.objection = await generateEmbedding(objectionText);
        embeddingTypes.push('objection');
        totalTokens += estimateTokens(objectionText);
      }
    }

    // 3. Persona embedding (persona-specific vocabulary)
    const personaText = buildPersonaText(responseUnit);
    if (personaText) {
      embeddings.persona = await generateEmbedding(personaText);
      embeddingTypes.push('persona');
      totalTokens += estimateTokens(personaText);
    }

    // Store embeddings in database
    const updateData = {};
    if (embeddings.content) updateData.content_embedding = embeddings.content;
    if (embeddings.objection) updateData.objection_embedding = embeddings.objection;
    if (embeddings.persona) updateData.persona_embedding = embeddings.persona;

    const { error: updateError } = await supabase
      .from('response_units')
      .update(updateData)
      .eq('id', responseUnitId);

    if (updateError) throw updateError;

    // Track embedding metadata
    const cost = (totalTokens / 1000) * COST_PER_1K_TOKENS;
    
    for (const type of embeddingTypes) {
      await supabase
        .from('embedding_metadata')
        .upsert({
          response_unit_id: responseUnitId,
          embedding_type: type,
          model: EMBEDDING_MODEL,
          dimension: EMBEDDING_DIMENSION,
          generation_cost: cost / embeddingTypes.length,
          generated_at: new Date().toISOString()
        });
    }

    return {
      responseUnitId,
      embeddingTypes,
      totalTokens,
      estimatedCost: cost,
      success: true
    };

  } catch (error) {
    console.error(`Error generating embeddings for ${responseUnitId}:`, error);
    throw error;
  }
};

/**
 * Build content text from response unit for embedding
 * @param {Object} responseUnit - Response unit data
 * @returns {string} - Text for embedding
 */
const buildContentText = (responseUnit) => {
  const parts = [
    responseUnit.title || '',
    responseUnit.phases?.acknowledge?.statement || '',
    responseUnit.phases?.probe?.questions?.join(' ') || '',
    responseUnit.phases?.value?.statement || '',
    responseUnit.phases?.nextStep?.cta || '',
    responseUnit.tags?.join(' ') || ''
  ];

  return parts.filter(p => p).join(' ').trim();
};

/**
 * Build objection-specific text for embedding
 * @param {Object} responseUnit - Response unit data
 * @returns {string} - Objection text for embedding
 */
const buildObjectionText = (responseUnit) => {
  const parts = [
    responseUnit.title || '',
    responseUnit.phases?.acknowledge?.statement || '',
    responseUnit.metadata?.objection_type || '',
    'objection handling response'
  ];

  return parts.filter(p => p).join(' ').trim();
};

/**
 * Build persona-specific text for embedding
 * @param {Object} responseUnit - Response unit data
 * @returns {string} - Persona text for embedding
 */
const buildPersonaText = (responseUnit) => {
  const persona = responseUnit.metadata?.persona || '';
  const personaVocabulary = {
    CONSULTATIVE: 'collaborative partnership consultative advisory trusted advisor customer-centric',
    CHALLENGER: 'provocative insight challenge reframe push back disruptive teaching',
    RELATIONSHIP: 'rapport connection trust relationship warm personal empathetic',
    EDUCATOR: 'teaching educational informative guidance expertise knowledge sharing',
    REACTIVE: 'responsive reactive answering addressing following customer-led'
  };

  const vocab = personaVocabulary[persona] || '';
  const content = responseUnit.phases?.acknowledge?.statement || '';

  return `${persona} ${vocab} ${content}`.trim();
};

/**
 * Estimate token count for text
 * @param {string} text - Text to estimate
 * @returns {number} - Estimated tokens
 */
const estimateTokens = (text) => {
  // Rough estimate: 1 token ≈ 4 characters
  return Math.ceil(text.length / 4);
};

/**
 * Batch generate embeddings for multiple response units
 * @param {Array<Object>} responseUnits - Array of response units
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} - Summary statistics
 */
export const batchGenerateEmbeddings = async (responseUnits, onProgress) => {
  const results = {
    total: responseUnits.length,
    success: 0,
    failed: 0,
    totalCost: 0,
    errors: []
  };

  for (let i = 0; i < responseUnits.length; i += BATCH_SIZE) {
    const batch = responseUnits.slice(i, i + BATCH_SIZE);
    
    for (const unit of batch) {
      try {
        const result = await generateResponseUnitEmbeddings(unit.id, unit);
        results.success++;
        results.totalCost += result.estimatedCost;
        
        if (onProgress) {
          onProgress({
            current: results.success + results.failed,
            total: results.total,
            responseUnitId: unit.id,
            status: 'success'
          });
        }
      } catch (error) {
        results.failed++;
        results.errors.push({
          responseUnitId: unit.id,
          error: error.message
        });
        
        if (onProgress) {
          onProgress({
            current: results.success + results.failed,
            total: results.total,
            responseUnitId: unit.id,
            status: 'failed',
            error: error.message
          });
        }
      }
    }

    // Rate limiting: Wait 1 second between batches
    if (i + BATCH_SIZE < responseUnits.length) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }

  return results;
};

/**
 * Find semantically similar responses using embeddings
 * @param {string} queryText - Text to search for
 * @param {Object} options - Search options
 * @returns {Promise<Array>} - Similar responses with similarity scores
 */
export const findSimilarResponses = async (queryText, options = {}) => {
  const {
    type = null,
    threshold = 0.7,
    limit = 10
  } = options;

  try {
    // Generate embedding for query
    const queryEmbedding = await generateEmbedding(queryText);

    // Call database function for similarity search
    const { data, error } = await supabase.rpc('get_similar_responses', {
      query_embedding: queryEmbedding,
      match_threshold: threshold,
      match_count: limit,
      response_type: type
    });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error finding similar responses:', error);
    throw error;
  }
};

/**
 * Generate embedding for user transcript
 * @param {string} transcript - User's spoken/written response
 * @returns {Promise<Array<number>>} - Embedding vector
 */
export const generateTranscriptEmbedding = async (transcript) => {
  return await generateEmbedding(transcript);
};

export default {
  generateEmbedding,
  generateEmbeddingsBatch,
  generateResponseUnitEmbeddings,
  batchGenerateEmbeddings,
  findSimilarResponses,
  generateTranscriptEmbedding
};
